import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Hash, Loader2 } from "lucide-react";
import api from "../api";
import { useLanguage } from "../i18n";
import { usePageMeta } from "../hooks/usePageMeta";
import { Reveal } from "../components/Reveal";
import { BlogPromoCard } from "../components/BlogPromoCard";
import { BlogSidebar } from "../components/BlogSidebar";

const imgSrc = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

const stableIndex = (seed, modulo) => {
  if (!modulo) return 0;
  let hash = 0;
  for (const ch of String(seed || "")) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return hash % modulo;
};

const normalizeText = (value) => String(value || "")
  .toLowerCase()
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "");

const matchesOffer = (offer, text) => {
  const name = normalizeText(offer.name || "");
  const localized = normalizeText(offer.localized_name || "");
  return [name, localized].some((n) => n.length > 2 && text.includes(n));
};

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, lang, regionName } = useLanguage();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);
  const [posts, setPosts] = useState([]);
  const [offers, setOffers] = useState([]);
  const [customPromo, setCustomPromo] = useState(null);
  const [readProgress, setReadProgress] = useState(0);
  const articleRef = useRef(null);
  const touchStart = useRef(null);

  const absImg = post?.image
    ? (post.image.startsWith("http") ? post.image : `${process.env.REACT_APP_BACKEND_URL}${post.image}`)
    : undefined;
  usePageMeta(post ? post.title : "Blog", post ? post.excerpt : "", undefined, absImg);

  useEffect(() => {
    setPost(null);
    setMissing(false);
    api.get(`/blog/posts/${slug}`).then(({ data }) => setPost(data.data)).catch(() => setMissing(true));
  }, [slug]);

  useEffect(() => {
    api.get(`/blog/posts?lang=${lang}&limit=24`).then(({ data }) => setPosts(data.data || [])).catch(() => setPosts([]));
  }, [lang]);

  useEffect(() => {
    api.get("/destinations?type=local").then(({ data }) => setOffers(data.data || [])).catch(() => setOffers([]));
  }, []);

  // Promo personalizzata (Admin → Marketing): se attiva sostituisce le offerte automatiche
  useEffect(() => {
    api.get("/marketing/public").then(({ data }) => setCustomPromo(data.data?.blog_promo || null)).catch(() => {});
  }, []);

  const paragraphs = useMemo(
    () => (post?.content || "").split(/\n{2,}/).map((p) => p.trim()).filter(Boolean),
    [post]
  );

  const promoOffers = useMemo(() => {
    // Promo personalizzata da Admin → Marketing: sostituisce le offerte automatiche
    if (customPromo) return [{ custom: customPromo }];
    const pool = offers
      .filter((o) => o.code && o.min_price != null)
      .map((o) => ({ ...o, localized_name: regionName(o.code) }));
    if (!pool.length || !post) return [];

    // Prima tag/titolo, poi contenuto: la promo segue il Paese citato dall'articolo.
    const priorityText = normalizeText([post.title, ...(post.tags || [])].join(" "));
    const fullText = normalizeText([post.title, post.excerpt, post.content, ...(post.tags || [])].join(" "));
    const priorityMatches = pool.filter((o) => matchesOffer(o, priorityText));
    const contentMatches = pool.filter((o) => matchesOffer(o, fullText));
    const source = priorityMatches.length ? priorityMatches : (contentMatches.length ? contentMatches : pool);
    const first = source[stableIndex(slug, source.length)];
    const secondPool = source.filter((o) => o.code !== first.code);
    const fallbackPool = pool.filter((o) => o.code !== first.code);
    const secondSource = secondPool.length ? secondPool : fallbackPool;
    const second = secondSource[stableIndex(`${slug}-second`, secondSource.length)];
    return second ? [first, second] : [first];
  }, [offers, post, slug, regionName, customPromo]);

  const articleNav = useMemo(() => {
    const idx = posts.findIndex((p) => p.post_id === post?.post_id);
    if (idx < 0) return { prev: null, next: null };
    return {
      prev: idx > 0 ? posts[idx - 1] : null,       // articolo più recente
      next: idx < posts.length - 1 ? posts[idx + 1] : null,  // articolo precedente nel tempo
    };
  }, [posts, post]);

  useEffect(() => {
    if (!post) return undefined;
    const update = () => {
      const el = articleRef.current;
      if (!el) return;
      const start = el.offsetTop;
      const end = start + el.offsetHeight - window.innerHeight;
      const pct = end <= start ? 1 : (window.scrollY - start) / (end - start);
      setReadProgress(Math.max(0, Math.min(1, pct)));
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [post]);

  const onTouchStart = (e) => {
    const touch = e.touches[0];
    touchStart.current = { x: touch.clientX, y: touch.clientY };
  };

  const onTouchEnd = (e) => {
    if (!touchStart.current) return;
    const touch = e.changedTouches[0];
    const dx = touch.clientX - touchStart.current.x;
    const dy = touch.clientY - touchStart.current.y;
    touchStart.current = null;
    if (Math.abs(dx) < 80 || Math.abs(dy) > 60) return;
    if (dx < 0 && articleNav.next) navigate(`/blog/${articleNav.next.slug}`);
    if (dx > 0 && articleNav.prev) navigate(`/blog/${articleNav.prev.slug}`);
  };

  const firstPromoAfter = paragraphs.length > 1 ? 1 : 0;
  const secondPromoAfter = 4;

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString(lang, { day: "numeric", month: "long", year: "numeric" }); }
    catch { return ""; }
  };

  return (
    <div data-testid="blog-post-page" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
      className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 sm:py-20">
      {post && (
        <div data-testid="reading-progress-bar" aria-hidden="true"
          className="fixed top-0 left-0 z-50 h-1 bg-[#FF6B35] transition-[width] duration-150"
          style={{ width: `${readProgress * 100}%` }} />
      )}
      <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-2 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-[#FF6B35] transition-colors">
        <ArrowLeft className="w-3.5 h-3.5" /> Blog
      </Link>
      {missing ? (
        <p data-testid="blog-post-404" className="mt-16 text-center text-slate-400">{t("blog.notFound")}</p>
      ) : !post ? (
        <div className="py-20 text-center"><Loader2 className="w-7 h-7 animate-spin mx-auto text-[#FF6B35]" /></div>
      ) : (
        <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-10 items-start">
          <Reveal>
            <article ref={articleRef} className="max-w-3xl">
              {post.image && (
                <img data-testid="blog-post-image" src={imgSrc(post.image)} alt={post.title}
                  className="mt-8 w-full max-h-96 object-cover rounded-3xl border border-slate-100" />
              )}
              <div className="mt-8 flex flex-wrap gap-1.5">
                {(post.categories || []).map((c) => (
                  <Link key={c} to="/blog" className="rounded-full bg-[#FAF5EE] px-3 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest text-[#FF6B35]">{c}</Link>
                ))}
              </div>
              <h1 className="mt-5 font-display font-extrabold tracking-tight text-3xl sm:text-5xl text-slate-900 text-balance">{post.title}</h1>
              <div className="mt-4 font-mono-t text-[10px] uppercase tracking-widest text-slate-400">{fmtDate(post.publish_at || post.created_at)}</div>
              <div data-testid="blog-post-content" className="mt-10 text-slate-700 leading-relaxed text-base sm:text-lg">
                {paragraphs.map((paragraph, i) => (
                  <Fragment key={`${post.post_id}-${i}`}>
                    <p className="mb-6 whitespace-pre-line">{paragraph}</p>
                    {i === firstPromoAfter && promoOffers[0] && (
                      <BlogPromoCard offer={promoOffers[0]} placement="mid" />
                    )}
                    {paragraphs.length > 5 && i === secondPromoAfter && promoOffers[1] && (
                      <BlogPromoCard offer={promoOffers[1]} placement="end" />
                    )}
                  </Fragment>
                ))}
              </div>
              {(post.tags || []).length > 0 && (
                <div className="mt-10 flex flex-wrap gap-2" data-testid="blog-post-tags">
                  {post.tags.map((tag) => (
                    <Link key={tag} to={`/blog?tag=${encodeURIComponent(tag)}`} data-testid={`blog-post-tag-${tag}`}
                      className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 font-mono-t text-[10px] uppercase tracking-widest text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                      <Hash className="w-3 h-3" />{tag}
                    </Link>
                  ))}
                </div>
              )}
              <div className="mt-14 rounded-3xl bg-[#E6F7F5] p-8 text-center">
                <h3 className="font-display font-bold text-xl text-slate-900">{t("blog.ctaT")}</h3>
                <Link to="/destinations" data-testid="blog-post-cta"
                  className="mt-4 inline-block rounded-full bg-[#FF6B35] text-white px-8 py-3.5 font-mono-t text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#E8551F] transition-colors">
                  {t("featured.viewAll")}
                </Link>
              </div>
              {(articleNav.prev || articleNav.next) && (
                <div data-testid="article-mobile-nav" className="lg:hidden mt-8 rounded-3xl bg-white border border-slate-200/80 p-4">
                  <p className="text-center font-mono-t text-[9px] font-bold uppercase tracking-[0.22em] text-slate-400">
                    {t("blog.swipeHint")}
                  </p>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    {articleNav.prev ? (
                      <Link to={`/blog/${articleNav.prev.slug}`} data-testid="article-mobile-prev"
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 px-4 py-2.5 font-mono-t text-[9px] font-bold uppercase tracking-[0.14em] text-slate-600 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                        <ArrowLeft className="w-3.5 h-3.5" /> {t("blog.prevArticle")}
                      </Link>
                    ) : <span />}
                    {articleNav.next ? (
                      <Link to={`/blog/${articleNav.next.slug}`} data-testid="article-mobile-next"
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 px-4 py-2.5 font-mono-t text-[9px] font-bold uppercase tracking-[0.14em] text-slate-600 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                        {t("blog.nextArticle")} <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    ) : <span />}
                  </div>
                </div>
              )}
            </article>
          </Reveal>
          <BlogSidebar current={post} posts={posts} />
        </div>
      )}
    </div>
  );
}
